import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  Input,
  inject,
  type OnChanges,
  type OnInit,
  output,
  type SimpleChanges,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ChevronDown, ChevronUp, LucideAngularModule } from 'lucide-angular';
import type { EventType } from '../../../server/models/event-item.model';
import type { UpdateItem } from '../../core/models/update-item.model';
import { EventService } from '../../core/services/event.service';
import { UpdateService } from '../../core/services/update.service';
import { SharedCardComponent } from '../../shared/components/shared-card/shared-card.component';
import { TimeAgoPipe } from '../../shared/pipes/time-ago.pipe';

@Component({
  selector: 'app-updates',
  standalone: true,
  imports: [CommonModule, TimeAgoPipe, LucideAngularModule, SharedCardComponent],
  templateUrl: './updates.component.html',
})
export class UpdatesComponent implements OnInit, OnChanges {
  @Input() eventDocumentId?: string;
  @Input() showExpandCollapse: boolean = false;
  @Input() compact: boolean = false;
  @Input() showOnlyImportant: boolean = false;
  @Input() showCard: boolean = false;
  @Input() cardClasses: string = 'p-6';
  hasUpdates = output<boolean>();

  // Icons
  chevronDown = ChevronDown;
  chevronUp = ChevronUp;

  // State
  isExpanded = signal(false);
  updateItems = signal<UpdateItem[]>([]);

  // Computed properties for latest update display
  latestUpdate = computed(() => {
    const items = this.updateItems();
    return items.length > 0 ? items[0] : null;
  });

  remainingUpdates = computed(() => {
    const items = this.updateItems();
    return items.length > 1 ? items.slice(1) : [];
  });

  remainingCount = computed(() => this.remainingUpdates().length);

  hasRemainingUpdates = computed(() => this.remainingCount() > 0);

  private route = inject(ActivatedRoute);
  private updateService = inject(UpdateService);
  private eventService = inject(EventService);

  ngOnInit(): void {
    // If no eventDocumentId is provided via @Input, try to get it from route
    if (!this.eventDocumentId) {
      const eventDocumentId = this.route.snapshot.params.eventDocumentId as string;
      const eventType = this.route.snapshot.params.eventType as EventType;
      if (!this.eventService.getCurrentEvent() || this.eventService.getCurrentEvent()?.documentId !== eventDocumentId) {
        this.eventService.getEvent(eventDocumentId, eventType).subscribe((value) => {
          this.eventService.setCurrentEvent(value);
          this.loadUpdates(eventDocumentId);
        });
      } else {
        this.loadUpdates(eventDocumentId);
      }
    } else {
      // eventDocumentId provided via @Input
      this.loadUpdates(this.eventDocumentId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to changes in @Input properties
    if (changes.eventDocumentId && this.eventDocumentId) {
      this.loadUpdates(this.eventDocumentId);
    }
  }

  private loadUpdates(eventId: string): void {
    this.updateService.getEventUpdates(eventId, this.showOnlyImportant).subscribe({
      next: (updates) => {
        this.updateItems.set(updates);
        if (updates.length > 0) {
          this.hasUpdates.emit(true);
        } else {
          this.hasUpdates.emit(false);
        }
      },
      error: (error: Error) => {
        console.error('Error fetching updates:', error);
        this.updateItems.set([]);
        this.hasUpdates.emit(false);
      },
    });
  }

  toggleExpanded(): void {
    this.isExpanded.set(!this.isExpanded());
  }
}
